import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { 
  Building2, Plus, Edit, Trash2, Layers, Store, MapPin, 
  ArrowLeft, Save, RefreshCw, Grid3X3, Eye, Users, Wrench
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MallManagementPage() {
  const { mallId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mall, setMall] = useState(null);
  const [floors, setFloors] = useState([]);
  const [units, setUnits] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedFloor, setSelectedFloor] = useState("all");
  
  // Dialogs
  const [showFloorDialog, setShowFloorDialog] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [editingFloor, setEditingFloor] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);

  // Form states
  const [floorForm, setFloorForm] = useState({ name: "", level: 0, total_area_sqft: 0 });
  const [unitForm, setUnitForm] = useState({
    unit_number: "",
    floor: 0,
    carpet_area_sqft: 500,
    buildup_area_sqft: 600,
    category: "vanilla",
    status: "vacant",
    rent_per_sqft: 0,
    cam_charges: 50,
    position_x: 0,
    position_y: 0,
    width: 10,
    depth: 8
  });

  useEffect(() => {
    if (mallId && token) {
      fetchMallData();
    }
  }, [mallId, token]);

  const fetchMallData = async () => {
    setLoading(true);
    try {
      // Fetch mall details
      const mallRes = await axios.get(`${API}/api/malls/${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMall(mallRes.data);

      // Fetch units for this mall
      const unitsRes = await axios.get(`${API}/api/shops?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnits(unitsRes.data);

      // Extract unique floors
      const floorLevels = [...new Set(unitsRes.data.map(u => u.floor))].sort((a, b) => a - b);
      const floorsData = floorLevels.map(level => {
        const floorUnits = unitsRes.data.filter(u => u.floor === level);
        return {
          level,
          name: `Floor ${level}`,
          unit_count: floorUnits.length,
          total_area: floorUnits.reduce((sum, u) => sum + (u.carpet_area_sqft || 0), 0),
          occupied: floorUnits.filter(u => u.status === "occupied").length,
          vacant: floorUnits.filter(u => u.status === "vacant").length
        };
      });
      setFloors(floorsData);

    } catch (err) {
      console.error("Failed to fetch mall data:", err);
      toast.error("Failed to load mall data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnit = async () => {
    try {
      const unitData = {
        ...unitForm,
        mall_id: mallId,
        buildup_area_sqft: unitForm.carpet_area_sqft * 1.2,
        position_z: unitForm.floor * 5,
        height: 4
      };

      if (editingUnit) {
        await axios.put(`${API}/api/shops/${editingUnit.id}`, unitForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Unit updated successfully");
      } else {
        await axios.post(`${API}/api/shops`, unitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Unit created successfully");
      }

      setShowUnitDialog(false);
      setEditingUnit(null);
      resetUnitForm();
      fetchMallData();
    } catch (err) {
      toast.error(editingUnit ? "Failed to update unit" : "Failed to create unit");
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!confirm("Are you sure you want to delete this unit?")) return;
    
    try {
      await axios.delete(`${API}/api/shops/${unitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Unit deleted");
      fetchMallData();
    } catch (err) {
      toast.error("Failed to delete unit");
    }
  };

  const handleBulkCreateUnits = async () => {
    const floor = parseInt(prompt("Enter floor number:", "0"));
    const count = parseInt(prompt("How many units to create?", "10"));
    
    if (isNaN(floor) || isNaN(count) || count < 1) {
      toast.error("Invalid input");
      return;
    }

    try {
      const units = [];
      const existingOnFloor = units.filter(u => u.floor === floor).length;
      
      for (let i = 0; i < count; i++) {
        const unitNum = existingOnFloor + i + 1;
        units.push({
          mall_id: mallId,
          unit_number: `F${floor}-${String(unitNum).padStart(3, '0')}`,
          floor: floor,
          carpet_area_sqft: 500 + Math.random() * 1000,
          buildup_area_sqft: 600 + Math.random() * 1200,
          category: ["vanilla", "anchor", "food_court", "kiosk"][Math.floor(Math.random() * 4)],
          status: "vacant",
          rent_per_sqft: 0,
          cam_charges: 50,
          position_x: (i % 10) * 12,
          position_y: floor * 5,
          position_z: Math.floor(i / 10) * 12,
          width: 10,
          depth: 8,
          height: 4
        });
      }

      await axios.post(`${API}/api/shops/bulk`, units, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Created ${count} units on Floor ${floor}`);
      fetchMallData();
    } catch (err) {
      toast.error("Failed to create units");
    }
  };

  const resetUnitForm = () => {
    setUnitForm({
      unit_number: "",
      floor: 0,
      carpet_area_sqft: 500,
      buildup_area_sqft: 600,
      category: "vanilla",
      status: "vacant",
      rent_per_sqft: 0,
      cam_charges: 50,
      position_x: 0,
      position_y: 0,
      width: 10,
      depth: 8
    });
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({
      unit_number: unit.unit_number,
      floor: unit.floor,
      carpet_area_sqft: unit.carpet_area_sqft,
      buildup_area_sqft: unit.buildup_area_sqft,
      category: unit.category,
      status: unit.status,
      rent_per_sqft: unit.rent_per_sqft,
      cam_charges: unit.cam_charges,
      position_x: unit.position_x,
      position_y: unit.position_y,
      width: unit.width,
      depth: unit.depth
    });
    setShowUnitDialog(true);
  };

  const filteredUnits = selectedFloor === "all" 
    ? units 
    : units.filter(u => u.floor === parseInt(selectedFloor));

  const getStatusColor = (status) => {
    const colors = {
      vacant: "bg-amber-500",
      occupied: "bg-green-500",
      reserved: "bg-blue-500",
      soft_block: "bg-purple-500",
      hard_block: "bg-red-500"
    };
    return colors[status] || "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mall-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/malls")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{mall?.name}</h1>
            <p className="text-muted-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {mall?.city}, {mall?.state}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/digital-twin/${mallId}`)}>
            <Eye className="h-4 w-4 mr-2" /> View 3D Twin
          </Button>
          <Button variant="outline" onClick={() => navigate(`/mall/${mallId}/operations`)}>
            <Wrench className="h-4 w-4 mr-2" /> Operations
          </Button>
          <Button onClick={() => navigate(`/mall/${mallId}/pre-leasing`)}>
            Pre-Leasing
          </Button>
          <Button onClick={() => navigate(`/mall/${mallId}/post-leasing`)}>
            Post-Leasing
          </Button>
        </div>
      </div>

      {/* Mall Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Floors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{floors.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{units.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Occupied</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {units.filter(u => u.status === "occupied").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Vacant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">
              {units.filter(u => u.status === "vacant").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="floors">Floors</TabsTrigger>
          <TabsTrigger value="units">Units</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Floors Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Floors Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {floors.map(floor => (
                    <div key={floor.level} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <span className="font-medium">{floor.name}</span>
                        <span className="text-muted-foreground ml-2">({floor.unit_count} units)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default">{floor.occupied} occupied</Badge>
                        <Badge variant="outline">{floor.vacant} vacant</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5" />
                  Category Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(
                    units.reduce((acc, u) => {
                      acc[u.category] = (acc[u.category] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="capitalize">{category.replace(/_/g, ' ')}</span>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Floors Tab */}
        <TabsContent value="floors" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Floor Management</CardTitle>
              <Button onClick={handleBulkCreateUnits}>
                <Plus className="h-4 w-4 mr-2" /> Bulk Add Units to Floor
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {floors.map(floor => (
                  <div key={floor.level} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">{floor.name}</h3>
                        <p className="text-muted-foreground">
                          {floor.total_area.toLocaleString()} sq ft total area
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedFloor(String(floor.level));
                            setActiveTab("units");
                          }}
                        >
                          View Units
                        </Button>
                      </div>
                    </div>
                    
                    {/* Mini floor map */}
                    <div className="grid grid-cols-10 gap-1">
                      {units.filter(u => u.floor === floor.level).slice(0, 20).map(unit => (
                        <div 
                          key={unit.id}
                          className={`h-8 rounded ${getStatusColor(unit.status)} flex items-center justify-center text-xs text-white font-medium`}
                          title={`${unit.unit_number} - ${unit.brand_name || 'Vacant'}`}
                        >
                          {unit.unit_number.split('-')[1]}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {floors.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No floors yet. Add units to create floor structure.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle>Unit Management</CardTitle>
                <Select value={selectedFloor} onValueChange={setSelectedFloor}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by floor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Floors</SelectItem>
                    {floors.map(f => (
                      <SelectItem key={f.level} value={String(f.level)}>
                        Floor {f.level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => { resetUnitForm(); setEditingUnit(null); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Unit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingUnit ? "Edit Unit" : "Add New Unit"}</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Unit Number</Label>
                        <Input 
                          value={unitForm.unit_number}
                          onChange={(e) => setUnitForm({...unitForm, unit_number: e.target.value})}
                          placeholder="F0-001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Floor</Label>
                        <Input 
                          type="number"
                          value={unitForm.floor}
                          onChange={(e) => setUnitForm({...unitForm, floor: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Carpet Area (sq ft)</Label>
                        <Input 
                          type="number"
                          value={unitForm.carpet_area_sqft}
                          onChange={(e) => setUnitForm({...unitForm, carpet_area_sqft: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select 
                          value={unitForm.category}
                          onValueChange={(v) => setUnitForm({...unitForm, category: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="anchor">Anchor</SelectItem>
                            <SelectItem value="vanilla">Vanilla</SelectItem>
                            <SelectItem value="food_court">Food Court</SelectItem>
                            <SelectItem value="kiosk">Kiosk</SelectItem>
                            <SelectItem value="entertainment">Entertainment</SelectItem>
                            <SelectItem value="services">Services</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select 
                          value={unitForm.status}
                          onValueChange={(v) => setUnitForm({...unitForm, status: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="vacant">Vacant</SelectItem>
                            <SelectItem value="occupied">Occupied</SelectItem>
                            <SelectItem value="reserved">Reserved</SelectItem>
                            <SelectItem value="soft_block">Soft Block</SelectItem>
                            <SelectItem value="hard_block">Hard Block</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>CAM Charges (per sq ft)</Label>
                        <Input 
                          type="number"
                          value={unitForm.cam_charges}
                          onChange={(e) => setUnitForm({...unitForm, cam_charges: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Position X</Label>
                        <Input 
                          type="number"
                          value={unitForm.position_x}
                          onChange={(e) => setUnitForm({...unitForm, position_x: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Position Y</Label>
                        <Input 
                          type="number"
                          value={unitForm.position_y}
                          onChange={(e) => setUnitForm({...unitForm, position_y: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowUnitDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateUnit}>
                      <Save className="h-4 w-4 mr-2" /> {editingUnit ? "Update" : "Create"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredUnits.map(unit => (
                  <div 
                    key={unit.id} 
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(unit.status)}`} />
                      <div>
                        <div className="font-medium">{unit.unit_number}</div>
                        <div className="text-sm text-muted-foreground">
                          Floor {unit.floor} • {unit.carpet_area_sqft?.toLocaleString()} sq ft • {unit.category}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {unit.brand_name && (
                        <Badge variant="secondary">{unit.brand_name}</Badge>
                      )}
                      <Badge variant={unit.status === "occupied" ? "default" : "outline"}>
                        {unit.status}
                      </Badge>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditUnit(unit)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteUnit(unit.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredUnits.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No units found. Create units to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
